import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import './ZZPShared.css';
import './Opdrachtgevers.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, { ...options, headers, credentials: 'include' });
}

const EMPTY = {
  naam: '',
  adres: '',
  postcode: '',
  plaats: '',
  kvk: '',
  vertegenwoordiger: '',
};

export default function Opdrachtgevers() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [error, setError]         = useState('');
  const [saving, setSaving]       = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url  = showInactive ? '/api/zzp/opdrachtgevers?all=1' : '/api/zzp/opdrachtgevers';
      const res  = await authFetch(url);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [showInactive]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setError('');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      naam: row.naam || '',
      adres: row.adres || '',
      postcode: row.postcode || '',
      plaats: row.plaats || '',
      kvk: row.kvk || '',
      vertegenwoordiger: row.vertegenwoordiger || '',
    });
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(EMPTY);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.naam.trim()) {
      setError('Naam is verplicht');
      return;
    }
    setSaving(true);
    const payload = {
      naam: form.naam.trim(),
      adres: form.adres.trim() || null,
      postcode: form.postcode.trim() || null,
      plaats: form.plaats.trim() || null,
      kvk: form.kvk.trim() || null,
      vertegenwoordiger: form.vertegenwoordiger.trim() || null,
    };
    try {
      const url    = editing ? `/api/zzp/opdrachtgevers/${editing.id}` : '/api/zzp/opdrachtgevers';
      const method = editing ? 'PUT' : 'POST';
      const res    = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Opslaan mislukt');
      }
      await load();
      closeModal();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleDelete = async (row, e) => {
    e.stopPropagation();
    if (!confirm(`Opdrachtgever "${row.naam}" deactiveren?`)) return;
    try {
      const res = await authFetch(`/api/zzp/opdrachtgevers/${row.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="zzp-page">
      <div className="zzp-header">
        <div>
          <h2>Opdrachtgevers</h2>
          <p>Beheer opdrachtgevers die je in contracten kunt selecteren</p>
        </div>
        <button className="zzp-primary-btn" onClick={openCreate}>
          <Plus size={18} />
          Nieuwe opdrachtgever
        </button>
      </div>

      <div className="og-toolbar">
        <label className="og-toggle">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Toon inactieve opdrachtgevers
        </label>
      </div>

      {loading ? (
        <div className="zzp-empty">Laden...</div>
      ) : items.length === 0 ? (
        <div className="zzp-empty">Nog geen opdrachtgevers. Klik op "Nieuwe opdrachtgever" om te beginnen.</div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="zzp-card-list">
            {items.map((row) => (
              <div key={row.id} className={`zzp-card${row.is_active === 0 ? ' og-card-inactive' : ''}`} onClick={() => openEdit(row)}>
                <div className="zzp-card-top">
                  <span className="zzp-card-name">{row.naam}</span>
                  <div className="zzp-actions" onClick={(e) => e.stopPropagation()}>
                    <button className="zzp-icon-btn" onClick={() => openEdit(row)} title="Bewerken">
                      <Pencil size={14} />
                    </button>
                    {row.is_active !== 0 && (
                      <button className="zzp-icon-btn danger" onClick={(e) => handleDelete(row, e)} title="Deactiveren">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {row.plaats && <div className="zzp-card-row"><span className="zzp-card-label">Plaats</span> {row.plaats}</div>}
                {row.adres && <div className="zzp-card-row"><span className="zzp-card-label">Adres</span> {row.adres}</div>}
                {row.postcode && <div className="zzp-card-row"><span className="zzp-card-label">Postcode</span> {row.postcode}</div>}
                {row.kvk && <div className="zzp-card-row"><span className="zzp-card-label">KvK</span> {row.kvk}</div>}
                {row.vertegenwoordiger && <div className="zzp-card-row"><span className="zzp-card-label">Vertegenwoordiger</span> {row.vertegenwoordiger}</div>}
                {row.is_active === 0 && <div className="og-inactive-badge">Inactief</div>}
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="zzp-table-wrap">
            <table className="zzp-table">
              <thead>
                <tr>
                  <th>Naam</th>
                  <th>Plaats</th>
                  <th>Vertegenwoordiger</th>
                  <th>Actief</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} onClick={() => openEdit(row)} className={`zzp-row${row.is_active === 0 ? ' og-row-inactive' : ''}`}>
                    <td className="zzp-cell-strong">{row.naam}</td>
                    <td>{row.plaats || '—'}</td>
                    <td>{row.vertegenwoordiger || '—'}</td>
                    <td>{row.is_active === 0 ? '—' : 'Ja'}</td>
                    <td>
                      <div className="zzp-actions" onClick={(e) => e.stopPropagation()}>
                        <button className="zzp-icon-btn" onClick={() => openEdit(row)} title="Bewerken">
                          <Pencil size={14} />
                        </button>
                        {row.is_active !== 0 && (
                          <button className="zzp-icon-btn danger" onClick={(e) => handleDelete(row, e)} title="Deactiveren">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modalOpen && (
        <div className="zzp-modal-overlay" onClick={closeModal}>
          <div className="zzp-modal-content og-modal" onClick={(e) => e.stopPropagation()}>
            <div className="zzp-modal-header">
              <h3>{editing ? 'Opdrachtgever bewerken' : 'Nieuwe opdrachtgever'}</h3>
              <button className="zzp-modal-close" onClick={closeModal}>✕</button>
            </div>

            <form onSubmit={handleSubmit} className="zzp-form">
              <div className="zzp-form-group">
                <label htmlFor="naam">Naam *</label>
                <input
                  id="naam"
                  type="text"
                  name="naam"
                  placeholder="Entice B.V."
                  value={form.naam}
                  onChange={handleChange}
                  autoFocus
                />
              </div>

              <div className="zzp-form-group">
                <label htmlFor="adres">Adres</label>
                <input
                  id="adres"
                  type="text"
                  name="adres"
                  placeholder="Voorbeeldstraat 1"
                  value={form.adres}
                  onChange={handleChange}
                />
              </div>

              <div className="zzp-form-row og-row-2">
                <div className="zzp-form-group">
                  <label htmlFor="postcode">Postcode</label>
                  <input
                    id="postcode"
                    type="text"
                    name="postcode"
                    placeholder="1234 AB"
                    value={form.postcode}
                    onChange={handleChange}
                  />
                </div>
                <div className="zzp-form-group">
                  <label htmlFor="plaats">Plaats</label>
                  <input
                    id="plaats"
                    type="text"
                    name="plaats"
                    placeholder="Rotterdam"
                    value={form.plaats}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="zzp-form-group">
                <label htmlFor="kvk">KvK-nummer</label>
                <input
                  id="kvk"
                  type="text"
                  name="kvk"
                  placeholder="12345678"
                  value={form.kvk}
                  onChange={handleChange}
                />
              </div>

              <div className="zzp-form-group">
                <label htmlFor="vertegenwoordiger">Vertegenwoordiger</label>
                <input
                  id="vertegenwoordiger"
                  type="text"
                  name="vertegenwoordiger"
                  placeholder="Naam vertegenwoordiger"
                  value={form.vertegenwoordiger}
                  onChange={handleChange}
                />
              </div>

              {error && <div className="zzp-form-error">{error}</div>}

              <div className="zzp-form-actions">
                <button type="button" className="zzp-btn-cancel" onClick={closeModal}>
                  Annuleren
                </button>
                <button type="submit" className="zzp-btn-submit" disabled={saving}>
                  {saving ? 'Opslaan...' : (editing ? 'Opslaan' : 'Toevoegen')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
